import { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Crown,
  ShieldCheck,
  Shield,
  Eye,
  MoreVertical,
  UserMinus,
  UserPlus,
  UserX,
  UserCheck,
  ArrowLeftRight,
  AlertCircle,
  Users,
  X,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { api, parseApiError, type OrganizationRole, type ScopedOrgMember, type OrgBan } from '../lib/api';
import { orgRoleDisplayName } from '../lib/roles';
import { isImportInFlight, mergeMemberPages, normalizeOrgMembersResponse } from '../lib/orgMembersPaging';
import { Card } from './Card';
import { ConfirmModal } from './ConfirmModal';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { usePageActions } from '../lib/pageActions';
import { useSectionHighlightContext } from '../lib/sectionHighlight';

// ── Constants ─────────────────────────────────────────────────────────────────

const ADMIN_SEAT_CAP = 4;

// ── Helpers ───────────────────────────────────────────────────────────────────

function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

function formatJoined(ts?: number): string | null {
  if (!ts) return null;
  return new Date(ts).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function roleIcon(role: OrganizationRole) {
  switch (role) {
    case 'ORG_OWNER': return Crown;
    case 'ORG_ADMIN': return ShieldCheck;
    case 'ORG_MEMBER': return Shield;
    case 'ORG_VIEWER': return Eye;
    default: return Shield;
  }
}

function roleLabel(role: OrganizationRole): string {
  return orgRoleDisplayName(role);
}

// ── OrgMemberRow — per-row sub-component (unexported) ─────────────────────────
// Self-contained: owns its own menu state + ref so click-outside detection
// works correctly regardless of list size.

type RowProps = {
  member: ScopedOrgMember;
  orgId: string;
  isSelf: boolean;
  canManageMembers: boolean;
  canChangeRoles: boolean;
  canTransfer: boolean;
  adminAtCap: boolean;
  index: number;
  onRemoveRequest: (m: ScopedOrgMember) => void;
  onTransferRequest: (m: ScopedOrgMember) => void;
  onBanRequest: (m: ScopedOrgMember) => void;
  onRoleError: (msg: string) => void;
};

function OrgMemberRow({
  member,
  orgId,
  isSelf,
  canManageMembers,
  canChangeRoles,
  canTransfer,
  adminAtCap,
  index,
  onRemoveRequest,
  onTransferRequest,
  onBanRequest,
  onRoleError,
}: RowProps) {
  const qc = useQueryClient();

  // ALL hooks at top before any early return
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const roleMut = useMutation({
    mutationFn: (role: OrganizationRole) =>
      api.updateOrgMemberRole(orgId, member.discordId, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgMembers', orgId] });
      setMenuOpen(false);
    },
    onError: (e: Error) => {
      onRoleError(parseApiError(e).message);
      setMenuOpen(false);
    },
  });

  const isOwnerRow = member.role === 'ORG_OWNER';
  const canRemove = canManageMembers && !isSelf && !isOwnerRow;
  // Role changes require org.manage_roles — ORG_OWNER + founder only (not ORG_ADMIN)
  const canEditRole = canChangeRoles && !isOwnerRow;
  // Transfer: actor is owner-or-founder, target is not self, target is not already the owner
  const canTransferToThis = canTransfer && !isSelf && !isOwnerRow;
  // Ban: any manager can ban, but not self or the owner
  const canBanThis = canManageMembers && !isSelf && !isOwnerRow;
  const showMenu = canRemove || canEditRole || canTransferToThis || canBanThis;
  const busy = roleMut.isPending;
  const RoleIcon = roleIcon(member.role);
  const joined = formatJoined(member.joinedAt);

  return (
    <div
      className={cn(
        `${SETTLE} group relative flex items-center justify-between border border-iron-800/60 bg-iron-950 px-4 py-3 transition-colors`,
        busy && 'opacity-60',
        !busy && 'hover:border-forge-900/60 hover:bg-iron-950'
      )}
      style={settleDelay(index)}
    >
      {/* Identity */}
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center border',
            isOwnerRow
              ? 'border-forge-500/40 bg-forge-500/10'
              : 'border-iron-700/50 bg-iron-950'
          )}
        >
          <RoleIcon
            className={cn(
              'h-4 w-4',
              isOwnerRow ? 'text-forge-300' : 'text-iron-400'
            )}
            strokeWidth={2.5}
          />
        </div>
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-black uppercase tracking-wide text-white">
            {member.displayName ?? member.gamertag ?? shortId(member.discordId)}
            {isSelf && (
              <span className="ml-2 font-mono text-[9px] font-normal text-iron-500">
                (you)
              </span>
            )}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-iron-500">
            {member.clanName && <span>{member.clanName}</span>}
            {joined && <span>{joined}</span>}
          </div>
        </div>
      </div>

      {/* Role badge + action menu */}
      <div className="flex shrink-0 items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 font-display text-[9px] font-black uppercase tracking-[0.18em] ring-1 ring-inset',
            isOwnerRow
              ? 'bg-forge-500/10 text-forge-300 ring-forge-500/30'
              : member.role === 'ORG_ADMIN'
              ? 'bg-amber-500/10 text-amber-300 ring-amber-500/30'
              : 'bg-iron-900 text-iron-300 ring-iron-600/40'
          )}
        >
          <RoleIcon className="h-2.5 w-2.5" strokeWidth={2.5} />
          {roleLabel(member.role)}
        </span>

        {showMenu && (
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen(o => !o)}
              disabled={busy}
              className="flex h-7 w-7 items-center justify-center border border-iron-800 text-iron-400 transition-colors hover:border-forge-500 hover:text-forge-200 disabled:opacity-50"
              aria-label="Member actions"
            >
              <MoreVertical className="h-3.5 w-3.5" strokeWidth={2.5} />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full z-40 mt-1 w-52 border border-forge-900/60 bg-iron-950 shadow-forged">
                <div className="border-b border-forge-900/40 px-3 py-2">
                  <p className="font-display text-[9px] font-black uppercase tracking-[0.22em] text-forge-400">
                    Actions
                  </p>
                </div>
                <div className="py-1">
                  {/* Role transitions — org.manage_roles only (ORG_OWNER + founder).
                      ORG_VIEWER folds into Member on the single role spine
                      (2026-07-24) — no more "promote viewer" action; a viewer
                      row (legacy data) reads and behaves as a Member already. */}
                  {canEditRole && (
                    <>
                      {/* ORG_MEMBER -> ORG_ADMIN (capped at 4; cap displayed inline) */}
                      {member.role === 'ORG_MEMBER' && (
                        <button
                          onClick={() => {
                            if (adminAtCap) {
                              onRoleError(
                                'Admin seat cap reached (max 4). Remove an admin first.'
                              );
                              setMenuOpen(false);
                              return;
                            }
                            setMenuOpen(false);
                            roleMut.mutate('ORG_ADMIN');
                          }}
                          disabled={adminAtCap}
                          title={adminAtCap ? 'Admin seats full — max 4' : undefined}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-iron-200 transition-colors hover:bg-iron-950 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <ShieldCheck
                            className="h-3 w-3 text-amber-400"
                            strokeWidth={2.5}
                          />
                          Promote to Admin
                          {adminAtCap && (
                            <span className="ml-auto text-[9px] text-amber-600">
                              cap
                            </span>
                          )}
                        </button>
                      )}
                      {/* ORG_ADMIN -> ORG_MEMBER */}
                      {member.role === 'ORG_ADMIN' && (
                        <button
                          onClick={() => {
                            setMenuOpen(false);
                            roleMut.mutate('ORG_MEMBER');
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-iron-200 transition-colors hover:bg-iron-950 hover:text-white"
                        >
                          <Shield className="h-3 w-3 text-iron-400" strokeWidth={2.5} />
                          Demote to Member
                        </button>
                      )}
                      {/* ORG_OWNER promotion is NEVER offered in this panel */}
                    </>
                  )}
                  {canTransferToThis && (
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        onTransferRequest(member);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-amber-300 transition-colors hover:bg-amber-950/40 hover:text-amber-200"
                    >
                      <ArrowLeftRight className="h-3 w-3" strokeWidth={2.5} />
                      Transfer ownership
                    </button>
                  )}
                  {canRemove && (
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        onRemoveRequest(member);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-300 transition-colors hover:bg-red-950/40 hover:text-red-200"
                    >
                      <UserMinus className="h-3 w-3" strokeWidth={2.5} />
                      Remove from org
                    </button>
                  )}
                  {canBanThis && (
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        onBanRequest(member);
                      }}
                      className="flex w-full items-center gap-2 border-t border-iron-800/40 px-3 py-2 text-left text-xs text-red-300 transition-colors hover:bg-red-950/60 hover:text-red-200"
                    >
                      <UserX className="h-3 w-3 text-red-400" strokeWidth={2.5} />
                      Ban from org
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── DiscordMember type (local) ─────────────────────────────────────────────────

type DiscordMember = { discordId: string; username: string; displayName: string; avatar: string | null };

// ── OrgMembersPanel — exported ────────────────────────────────────────────────

export function OrgMembersPanel({
  orgId,
  currentUserId,
  isFounder,
  currentRole,
}: {
  orgId: string;
  currentUserId: string;
  isFounder: boolean;
  currentRole: OrganizationRole;
}) {
  const qc = useQueryClient();

  // ── ALL hooks declared before any early return ────────────────────────────
  const [addOpen, setAddOpen] = useState(false);
  const [addRole, setAddRole] = useState<OrganizationRole>('ORG_MEMBER');
  const [addError, setAddError] = useState('');
  const [removeTarget, setRemoveTarget] = useState<ScopedOrgMember | null>(null);
  const [transferTarget, setTransferTarget] = useState<ScopedOrgMember | null>(null);
  const [banTarget, setBanTarget] = useState<ScopedOrgMember | null>(null);
  const [banReason, setBanReason] = useState('');
  const [unbanTarget, setUnbanTarget] = useState<OrgBan | null>(null);
  const [roleError, setRoleError] = useState('');

  // ── Pagination (T4 server-import contract) — page one comes off the
  // regular useQuery below; "Load more" pages accumulate in local state
  // since react-query's cache is keyed on (orgId), not (orgId, cursor).
  // Reset whenever orgId changes so switching orgs never leaks a stale
  // second-org's extra pages into the newly-mounted list.
  const [extraPages, setExtraPages] = useState<ScopedOrgMember[]>([]);
  const [moreCursor, setMoreCursor] = useState<string | undefined>(undefined);
  const [hasLoadedMore, setHasLoadedMore] = useState(false);
  useEffect(() => {
    setExtraPages([]);
    setMoreCursor(undefined);
    setHasLoadedMore(false);
  }, [orgId]);

  // @person picker state
  const [discordQuery, setDiscordQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [pickedMember, setPickedMember] = useState<DiscordMember | null>(null);

  // Authority — compute before guard so `enabled` can reference it
  const canManageMembers =
    currentRole === 'ORG_OWNER' || currentRole === 'ORG_ADMIN' || isFounder;
  // org.manage_roles: ORG_OWNER + founder only (ORG_ADMIN does NOT hold this cap)
  const canChangeRoles = currentRole === 'ORG_OWNER' || isFounder;
  // org.transfer_ownership: ORG_OWNER + founder only
  const canTransfer = currentRole === 'ORG_OWNER' || isFounder;

  // Page one — envelope-tolerant (see lib/orgMembersPaging.ts: bare array =
  // legacy, no nextCursor/importStatus). Polls at a modest 5s ONLY while a
  // background server import is QUEUED/RUNNING; stops itself the moment the
  // status flips to DONE/FAILED (or is simply absent, pre-backend-deploy).
  const { data: pageOneRaw, isLoading } = useQuery({
    queryKey: ['orgMembers', orgId],
    queryFn: () => api.orgMembers(orgId),
    enabled: canManageMembers,
    refetchInterval: query => (isImportInFlight(normalizeOrgMembersResponse(query.state.data).importStatus) ? 5000 : false),
  });
  const pageOne = useMemo(() => normalizeOrgMembersResponse(pageOneRaw), [pageOneRaw]);
  const members = useMemo(() => mergeMemberPages(pageOne.members, extraPages), [pageOne.members, extraPages]);
  const importStatus = pageOne.importStatus;
  // Once we've paged further, trust the cursor Load-more last returned
  // (page one's own nextCursor stays pinned to "after page one" on refetch).
  const activeNextCursor = hasLoadedMore ? moreCursor : pageOne.nextCursor;

  const loadMoreMut = useMutation({
    mutationFn: (cursor: string) => api.orgMembers(orgId, { cursor }),
    onSuccess: raw => {
      const env = normalizeOrgMembersResponse(raw);
      setExtraPages(prev => mergeMemberPages(prev, env.members));
      setMoreCursor(env.nextCursor);
      setHasLoadedMore(true);
    },
    onError: (e: Error) => setRoleError(parseApiError(e).message),
  });

  const { data: bansData } = useQuery({
    queryKey: ['orgBans', orgId],
    queryFn: () => api.orgBans(orgId),
    enabled: canManageMembers,
  });
  const bans = bansData?.bans ?? [];

  // Debounce the Discord search query (300ms, strip leading @)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(discordQuery.trim().replace(/^@+/, '')), 300);
    return () => clearTimeout(t);
  }, [discordQuery]);

  // Search org's Discord server members
  const memberSearch = useQuery({
    queryKey: ['orgDiscordMembers', orgId, debouncedQuery],
    queryFn: () => api.searchOrgDiscordMembers(orgId, debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  });

  const addMut = useMutation({
    mutationFn: ({
      discordId,
      role,
    }: {
      discordId: string;
      role: OrganizationRole;
    }) => api.addOrgMember(orgId, discordId, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgMembers', orgId] });
      setPickedMember(null);
      setDiscordQuery('');
      setDebouncedQuery('');
      setAddRole('ORG_MEMBER');
      setAddError('');
      setAddOpen(false);
    },
    onError: (e: Error) => setAddError(parseApiError(e).message),
  });

  const removeMut = useMutation({
    mutationFn: (discordId: string) => api.removeOrgMember(orgId, discordId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgMembers', orgId] });
      setRemoveTarget(null);
    },
    onError: (e: Error) => {
      setRoleError(parseApiError(e).message);
      setRemoveTarget(null);
    },
  });

  const transferMut = useMutation({
    mutationFn: (newOwnerDiscordId: string) =>
      api.transferOrgOwnership(orgId, newOwnerDiscordId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgMembers', orgId] });
      qc.invalidateQueries({ queryKey: ['me'] });
      setTransferTarget(null);
    },
    onError: (e: Error) => {
      setRoleError(parseApiError(e).message);
      setTransferTarget(null);
    },
  });

  const banMut = useMutation({
    mutationFn: ({ discordId, reason }: { discordId: string; reason?: string }) =>
      api.banOrgMember(orgId, discordId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgMembers', orgId] });
      qc.invalidateQueries({ queryKey: ['orgBans', orgId] });
      setBanTarget(null);
      setBanReason('');
    },
    onError: (e: Error) => {
      setRoleError(parseApiError(e).message);
      setBanTarget(null);
      setBanReason('');
    },
  });

  const unbanMut = useMutation({
    mutationFn: (discordId: string) => api.unbanOrgMember(orgId, discordId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgBans', orgId] });
      setUnbanTarget(null);
    },
    onError: (e: Error) => {
      setRoleError(parseApiError(e).message);
      setUnbanTarget(null);
    },
  });

  // Global bottom action-bar registration (Lisa, 2026-07-19) — the SAME
  // "Add member" affordance as the header button below (same addOpen state),
  // reachable from the bar wherever else on the page you've scrolled to.
  // Gated on the SAME canManageMembers this whole panel gates on — a caller
  // who'd hit the `return null` below contributes nothing to the bar either,
  // no separate role check needed (see lib/pageActions.tsx's role-awareness
  // contract). 'org-members' is registered as a highlight target by
  // TournamentSetup's OrgPane, wrapping this component's mount point.
  const highlight = useSectionHighlightContext();
  const addMemberActions = useMemo(
    () => (canManageMembers ? [{
      id: 'org-members-add',
      label: 'Add member',
      icon: UserPlus,
      onClick: () => {
        highlight?.highlightNow('org-members');
        setAddOpen(true);
        setAddError('');
      },
    }] : null),
    [canManageMembers, highlight],
  );
  usePageActions(addMemberActions);

  // Guard: non-managers see nothing (double safety — mount in MyOrg is already gated)
  if (!canManageMembers) return null;

  const adminCount = members.filter(m => m.role === 'ORG_ADMIN').length;
  const adminAtCap = adminCount >= ADMIN_SEAT_CAP;

  // ORG_ADMIN may only add MEMBER/VIEWER (backend canAssignRole blocks admin/owner tier)
  // ORG_OWNER + founder may also add ORG_ADMIN (subject to cap)
  const addableRoles: OrganizationRole[] = canChangeRoles
    ? ['ORG_ADMIN', 'ORG_MEMBER', 'ORG_VIEWER']
    : ['ORG_MEMBER', 'ORG_VIEWER'];

  const canAdd = pickedMember !== null && !addMut.isPending && !(addRole === 'ORG_ADMIN' && adminAtCap);

  return (
    <Card>
      {/* ── Header ── */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
          <Users className="h-4 w-4 text-forge-400" strokeWidth={2.5} />
          Org Members
          {!isLoading && (
            <span className="ml-1 font-mono text-[10px] font-normal text-iron-500">
              {members.length}
            </span>
          )}
          {canChangeRoles && !isLoading && adminCount > 0 && (
            <span className="font-mono text-[10px] font-normal text-iron-500">
              &middot; {adminCount}/{ADMIN_SEAT_CAP} admin
              {adminAtCap && (
                <span className="ml-1 text-amber-600">(cap)</span>
              )}
            </span>
          )}
        </h2>
        <button
          onClick={() => {
            setAddOpen(o => !o);
            setAddError('');
            if (addOpen) {
              setDiscordQuery('');
              setDebouncedQuery('');
              setPickedMember(null);
            }
          }}
          className={cn(
            `${MORPH} flex items-center gap-1.5 border px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider transition-colors`,
            addOpen
              ? 'border-iron-700 bg-iron-950 text-iron-300 hover:border-iron-600 hover:text-iron-200'
              : 'border-forge-900/60 bg-iron-950 text-forge-300 hover:border-forge-500 hover:text-forge-100'
          )}
        >
          {addOpen ? (
            <>
              <X className="h-3 w-3" strokeWidth={2.5} /> Cancel
            </>
          ) : (
            <>
              <UserPlus className="h-3 w-3" strokeWidth={2.5} /> Add member
            </>
          )}
        </button>
      </div>

      {/* ── Add member form (@person picker) ── */}
      {addOpen && (
        <div
          className={`${SETTLE} mb-4 space-y-3 border border-forge-900/40 bg-iron-950 p-4`}
        >
          <p className="font-display text-[9px] font-black uppercase tracking-[0.22em] text-forge-400">
            Add from Discord
          </p>

          {/* Search + dropdown */}
          <div className="relative">
            {pickedMember ? (
              /* Selected state — show picked person with clear button */
              <div className="flex items-center gap-2 border border-forge-500/40 bg-iron-950 px-3 py-2">
                {pickedMember.avatar ? (
                  <img
                    src={`https://cdn.discordapp.com/avatars/${pickedMember.discordId}/${pickedMember.avatar}.png?size=32`}
                    alt=""
                    className="h-6 w-6 shrink-0 rounded-full"
                  />
                ) : (
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-iron-700 font-display text-[9px] font-black text-iron-200">
                    {pickedMember.displayName.slice(0, 2).toUpperCase()}
                  </span>
                )}
                <span className="flex-1 truncate text-sm text-white">{pickedMember.displayName}</span>
                <button
                  onClick={() => { setPickedMember(null); setDiscordQuery(''); setDebouncedQuery(''); }}
                  className="text-iron-500 hover:text-iron-300"
                  aria-label="Clear selection"
                >
                  <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              /* Search input */
              <input
                value={discordQuery}
                onChange={e => { setDiscordQuery(e.target.value); setAddError(''); }}
                placeholder="Type a teammate's name (no @ needed)…"
                className="input w-full text-sm"
                disabled={addMut.isPending}
              />
            )}

            {/* Dropdown — only while searching (no picked person yet) */}
            {!pickedMember && debouncedQuery.length >= 2 && (
              <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto border border-iron-700 bg-iron-950 shadow-forged">
                {memberSearch.isFetching ? (
                  <p className="flex items-center gap-2 px-3 py-2 text-xs text-iron-400">
                    <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                    Searching…
                  </p>
                ) : memberSearch.isError ? (
                  /* MASKING TRAP FIX: surface the real error, never swallow it as "no results" */
                  <div className="flex items-center gap-2 px-3 py-2 text-xs text-red-300">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
                    {(memberSearch.error as Error).message}
                  </div>
                ) : (memberSearch.data?.members.length ?? 0) === 0 ? (
                  /* Only show "no one found" when the query succeeded and returned empty */
                  <p className="px-3 py-2 text-xs text-iron-500">
                    No one by that name in your server — check the spelling, or they may not have joined yet.
                  </p>
                ) : (
                  memberSearch.data!.members.map(m => (
                    <button
                      key={m.discordId}
                      onClick={() => { setPickedMember(m); setDiscordQuery(m.displayName); }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-iron-950"
                    >
                      {m.avatar ? (
                        <img
                          src={`https://cdn.discordapp.com/avatars/${m.discordId}/${m.avatar}.png?size=32`}
                          alt=""
                          className="h-6 w-6 shrink-0 rounded-full"
                        />
                      ) : (
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-iron-700 font-display text-[9px] font-black text-iron-200">
                          {m.displayName.slice(0, 2).toUpperCase()}
                        </span>
                      )}
                      <span className="flex-1 truncate text-sm text-iron-100">{m.displayName}</span>
                      <span className="font-display text-[9px] font-black uppercase tracking-wider text-forge-300">
                        Select
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Role select + Add button */}
          <div className="flex flex-wrap gap-2">
            <select
              value={addRole}
              onChange={e => setAddRole(e.target.value as OrganizationRole)}
              className="input text-sm"
            >
              {addableRoles.map(r => (
                <option key={r} value={r} disabled={r === 'ORG_ADMIN' && adminAtCap}>
                  {roleLabel(r)}
                  {r === 'ORG_ADMIN' && adminAtCap ? ' (cap)' : ''}
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                if (!pickedMember) return;
                addMut.mutate({ discordId: pickedMember.discordId, role: addRole });
              }}
              disabled={!canAdd}
              className="btn-primary flex items-center gap-1.5 disabled:opacity-50"
            >
              {addMut.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <UserPlus className="h-3.5 w-3.5" strokeWidth={2.5} />
              )}
              Add
            </button>
          </div>

          {addError && (
            <div className="flex items-center gap-2 text-xs text-red-300">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
              {addError}
            </div>
          )}
        </div>
      )}

      {/* ── Server import notice — quiet inline text, no severity-colour
          strips (house style). QUEUED/RUNNING polls itself via
          refetchInterval above; DONE shows nothing (fresh list lands on the
          next ordinary refetch). ── */}
      {importStatus && isImportInFlight(importStatus) && (
        <div className="mb-3 flex items-center gap-2 text-[11px] italic text-iron-500">
          <Loader2 className="h-3 w-3 shrink-0 animate-spin" strokeWidth={2.5} />
          Importing server members — {importStatus.membersImported} imported so far…
        </div>
      )}
      {importStatus?.state === 'FAILED' && (
        <div className="mb-3 flex items-center gap-2 text-[11px] italic text-iron-500">
          <AlertCircle className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          Server import failed{importStatus.error ? `: ${importStatus.error}` : '.'}
        </div>
      )}

      {/* ── Inline role/remove error (dismissible) ── */}
      {roleError && (
        <div className="mb-3 flex items-center gap-2 rounded bg-red-950/50 px-3 py-2 text-xs text-red-300">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          <span className="flex-1">{roleError}</span>
          <button
            onClick={() => setRoleError('')}
            className="ml-2 text-red-500 hover:text-red-300"
            aria-label="Dismiss error"
          >
            <X className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/* ── Loading state ── */}
      {isLoading && (
        <div className="flex items-center gap-2 py-8 text-iron-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-xs">Loading members…</span>
        </div>
      )}

      {/* ── Empty state ── */}
      {!isLoading && members.length === 0 && (
        <p className="py-6 text-center text-xs text-iron-500">No members found.</p>
      )}

      {/* ── Member list ── */}
      {!isLoading && members.length > 0 && (
        <div className="space-y-1">
          {members.map((member, i) => (
            <OrgMemberRow
              key={member.discordId}
              member={member}
              orgId={orgId}
              isSelf={member.discordId === currentUserId}
              canManageMembers={canManageMembers}
              canChangeRoles={canChangeRoles}
              canTransfer={canTransfer}
              adminAtCap={adminAtCap}
              index={i}
              onRemoveRequest={setRemoveTarget}
              onTransferRequest={setTransferTarget}
              onBanRequest={(m) => { setBanTarget(m); setBanReason(''); }}
              onRoleError={setRoleError}
            />
          ))}
        </div>
      )}

      {/* ── Load more (cursor pagination, T4) — same secondary-button
          language as the Unban control below; only rendered while the
          backend still has pages left. ── */}
      {!isLoading && activeNextCursor && (
        <div className="mt-3 flex justify-center">
          <button
            onClick={() => loadMoreMut.mutate(activeNextCursor)}
            disabled={loadMoreMut.isPending}
            className="flex items-center gap-1.5 border border-iron-700 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-400 transition-colors hover:border-iron-500 hover:text-iron-200 disabled:opacity-40"
          >
            {loadMoreMut.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
            ) : (
              <ChevronDown className="h-3 w-3" strokeWidth={2.5} />
            )}
            {loadMoreMut.isPending ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {/* ── Remove confirmation modal ── */}
      {removeTarget && (
        <ConfirmModal
          tone="danger"
          icon={UserMinus}
          title={`Remove ${removeTarget.displayName ?? removeTarget.gamertag ?? shortId(removeTarget.discordId)}?`}
          body="They lose access to this org immediately."
          confirmLabel="Remove"
          confirmIcon={UserMinus}
          pending={removeMut.isPending}
          onConfirm={() => removeMut.mutate(removeTarget.discordId)}
          onCancel={() => setRemoveTarget(null)}
        />
      )}

      {/* ── Transfer ownership modal ── */}
      {transferTarget && (
        <ConfirmModal
          tone="warn"
          icon={ArrowLeftRight}
          title={`Transfer ownership to ${transferTarget.displayName ?? transferTarget.gamertag ?? shortId(transferTarget.discordId)}?`}
          body="You'll become an Admin. This action cannot be undone without the new owner's consent."
          confirmLabel="Transfer"
          confirmIcon={ArrowLeftRight}
          pending={transferMut.isPending}
          onConfirm={() => transferMut.mutate(transferTarget.discordId)}
          onCancel={() => setTransferTarget(null)}
        />
      )}

      {/* ── Ban confirmation modal (with optional reason) ── */}
      {banTarget && (
        <ConfirmModal
          tone="danger"
          icon={UserX}
          title={`Ban ${banTarget.displayName ?? banTarget.gamertag ?? shortId(banTarget.discordId)} from org?`}
          body="They'll be removed immediately and blocked from rejoining. You can lift the ban from the banlist below."
          confirmLabel="Ban"
          confirmIcon={UserX}
          pending={banMut.isPending}
          onConfirm={() => banMut.mutate({ discordId: banTarget.discordId, reason: banReason.trim() || undefined })}
          onCancel={() => { setBanTarget(null); setBanReason(''); }}
        >
          <label className="mb-1.5 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
            Reason (optional)
          </label>
          <textarea
            value={banReason}
            onChange={e => setBanReason(e.target.value)}
            placeholder="e.g. repeated rule violations"
            rows={2}
            className="w-full resize-none border border-iron-700/60 bg-iron-950 px-3 py-2 text-xs text-iron-100 placeholder-iron-600 focus:border-iron-500 focus:outline-none"
          />
        </ConfirmModal>
      )}

      {/* ── Unban confirmation modal ── */}
      {unbanTarget && (
        <ConfirmModal
          tone="warn"
          icon={UserCheck}
          title={`Unban ${unbanTarget.displayName ?? unbanTarget.gamertag ?? shortId(unbanTarget.discordId)}?`}
          body="They'll be able to rejoin the org."
          confirmLabel="Unban"
          confirmIcon={UserCheck}
          pending={unbanMut.isPending}
          onConfirm={() => unbanMut.mutate(unbanTarget.discordId)}
          onCancel={() => setUnbanTarget(null)}
        />
      )}

      {/* ── Banlist section ── */}
      {bans.length > 0 && (
        <div className="mt-6 border-t border-iron-800/60 pt-4">
          <h3 className="mb-3 flex items-center gap-2 font-display text-[10px] font-black uppercase tracking-[0.22em] text-iron-400">
            <UserX className="h-3.5 w-3.5 text-red-500/70" strokeWidth={2.5} />
            Banned
            <span className="font-mono text-[10px] font-normal text-iron-500">{bans.length}</span>
          </h3>
          <div className="space-y-1">
            {bans.map(ban => (
              <div
                key={ban.discordId}
                className="flex items-center justify-between border border-red-900/30 bg-red-950/10 px-4 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate font-display text-xs font-black uppercase tracking-wide text-iron-300">
                    {ban.displayName ?? ban.gamertag ?? shortId(ban.discordId)}
                  </p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10px] text-iron-500">
                    {ban.reason && <span className="italic">{ban.reason}</span>}
                    <span>{new Date(ban.bannedAt).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  </div>
                </div>
                <button
                  onClick={() => setUnbanTarget(ban)}
                  disabled={unbanMut.isPending}
                  className="ml-3 flex shrink-0 items-center gap-1.5 border border-iron-700 px-2.5 py-1 font-display text-[9px] font-black uppercase tracking-wider text-iron-400 transition-colors hover:border-iron-500 hover:text-iron-200 disabled:opacity-40"
                >
                  <UserCheck className="h-3 w-3" strokeWidth={2.5} />
                  Unban
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
